import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../Api";
import { useAuth } from "../context/authcontext";
import { useFlash } from "../context/flashcontext";

export function useProjectTimesheetList(projectId) {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const { showFlash } = useFlash();
    const [timesheets, setTimesheets] = useState([]);
    const [allTimesheets, setAllTimesheets] = useState([]);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterUser, setFilterUser] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTimesheetId, setSelectedTimesheetId] = useState(null);

    const fetchTimesheets = async (status = filterStatus, user = filterUser, order = sortOrder) => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/timesheets/project/${projectId}?include=user,project`);
            const fetchedTimesheets = res.data;
            setAllTimesheets(fetchedTimesheets);

            // Apply filters
            let data = fetchedTimesheets;
            if (status) {
                data = data.filter(ts => ts.status === status);
            }
            if (user) {
                data = data.filter(ts => ts.user.name === user);
            }
            if (order === "asc") {
                data = data.sort((a, b) => a.week_number - b.week_number);
            } else {
                data = data.sort((a, b) => b.week_number - a.week_number);
            }

            setTimesheets(data);
            setError(null);
        } catch (err) {
            setError(t('errors.loadFailed'));
            console.error("[useProjectTimesheetList] error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimesheets(filterStatus, filterUser, sortOrder);
        // eslint-disable-next-line
    }, [projectId, filterStatus, filterUser, sortOrder]);

    const handleApprove = async (id) => {
        try {
            await api.put(`/timesheets/${id}/approve`);
            fetchTimesheets();
        } catch (err) {
            setError(t('errors.approveTimesheetFailed'));
        }
    };

    const handleReject = async (id) => {
        try {
            await api.put(`/timesheets/${id}/reject`);
            fetchTimesheets();
        } catch (err) {
            setError(t('errors.rejectTimesheetFailed'));
        }
    };

    const confirmDelete = (id) => {
        setSelectedTimesheetId(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirmed = async () => {
        try {
            await api.delete(`/timesheets/${selectedTimesheetId}`);
            setTimesheets(prev => prev.filter(ts => ts.id !== selectedTimesheetId));
            setAllTimesheets(prev => prev.filter(ts => ts.id !== selectedTimesheetId));
            showFlash(t('success.timesheetDeleted'), "success");
        } catch (err) {
            showFlash(t('errors.deleteTimesheetFailed'), "error");
        } finally {
            setShowDeleteModal(false);
            setSelectedTimesheetId(null);
        }
    };

    return {
        timesheets,
        allTimesheets,
        filterStatus,
        setFilterStatus,
        filterUser,
        setFilterUser,
        sortOrder,
        setSortOrder,
        loading,
        error,
        handleApprove,
        handleReject,
        confirmDelete,
        handleDeleteConfirmed,
        showDeleteModal,
        setShowDeleteModal,
        auth
    };
}

